import React, { useEffect, useRef, useState } from "react";
import { FaBars, FaBell, FaChevronDown, FaSignOutAlt, FaUserCircle, FaCog } from "react-icons/fa";
import { HiSearch } from "react-icons/hi";
import axios from "axios";
import { config } from "../../config";

// ─── logo edit modal (duplicated so DashboardNav can use it) ──────────────────

const LogoEditModal = ({ currentLogo, currentInitials, onClose, onSave }) => {
  const [preview, setPreview] = useState(currentLogo || "");
  const [textLogo, setTextLogo] = useState(currentInitials || "");
  const [file, setFile] = useState(null);

  const handleFileChange = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <h3 className="font-bold text-gray-900">Change Logo</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
        </div>
        <div className="p-5 space-y-4">
          <div className="flex items-center justify-center h-20 rounded-xl border-2 border-dashed border-gray-200 bg-gray-50">
            {preview ? (
              <img src={preview} alt="Logo preview" className="h-14 object-contain" />
            ) : (
              <span className="text-gray-400 text-sm">{textLogo || "No logo"}</span>
            )}
          </div>
          <div>
            <label className="block text-xs font-bold text-gray-600 uppercase mb-1">Text Logo (fallback)</label>
            <input value={textLogo} onChange={(e) => setTextLogo(e.target.value)} maxLength={6}
              placeholder="e.g. DA"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm font-bold text-center uppercase focus:border-indigo-500 focus:outline-none" />
          </div>
          <div>
            <label className="block text-xs font-bold text-gray-600 uppercase mb-1">Upload Image</label>
            <input type="file" accept="image/*" onChange={handleFileChange}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm file:mr-2 file:py-1 file:px-3 file:rounded file:border-0 file:text-xs file:font-bold file:bg-indigo-50 file:text-indigo-700" />
          </div>
        </div>
        <div className="px-5 py-4 bg-gray-50 flex gap-2 justify-end">
          <button onClick={onClose} className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-bold text-gray-600 hover:bg-gray-100">Cancel</button>
          <button onClick={() => onSave({ file, text: textLogo.trim() || "A" })}
            className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-bold text-white hover:bg-indigo-700">
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

const DashboardNav = ({ toggleSidebar }) => {
  const [user, setUser] = useState(null);
  const [profileOpen, setProfileOpen] = useState(false);
  const profileRef = useRef(null);

  const logoSrc = localStorage.getItem("sidebarLogoImg") || "";
  const logoInitials = localStorage.getItem("sidebarLogoText") || "A";
  const [showLogoEdit, setShowLogoEdit] = useState(false);

  const handleSaveLogo = ({ file, text }) => {
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const data = e.target.result;
        localStorage.setItem("sidebarLogoImg", data);
        localStorage.setItem("ieltsLogoImg", data);
      };
      reader.readAsDataURL(file);
    }
    localStorage.setItem("sidebarLogoText", text || "A");
    localStorage.setItem("ieltsLogoText", text || "A");
    window.dispatchEvent(new Event("logo-updated"));
  };

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return;
    axios
      .get(`${config.apiUrl}/user`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setUser(res.data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setProfileOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleLogout = async () => {
    const token = localStorage.getItem("token");
    try {
      await axios.post(`${config.apiUrl}/logout`, {}, { headers: { Authorization: `Bearer ${token}` } });
    } catch {}
    localStorage.removeItem("token");
    localStorage.removeItem("selectedMenu");
    localStorage.removeItem("expandedMenus");
    window.location.href = "/login";
  };

  const initials = user?.name
    ? user.name.split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase()
    : "AD";

  return (
    <>
    <header className="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-4 md:px-6 flex-shrink-0 z-20">
      {/* Left */}
      <div className="flex items-center gap-3">
        <button
          onClick={toggleSidebar}
          className="w-9 h-9 flex items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 transition-colors"
        >
          <FaBars className="text-lg" />
        </button>

        {/* Logo */}
        <button
          onClick={() => setShowLogoEdit(true)}
          title="Change logo"
          className="flex items-center gap-2 hover:opacity-80 transition-opacity rounded-lg px-2 py-1"
        >
          {logoSrc ? (
            <img src={logoSrc} alt="" className="h-9 w-9 object-contain rounded-lg" />
          ) : (
            <div className="w-9 h-9 rounded-lg bg-indigo-600 flex items-center justify-center font-bold text-white text-sm">
              {logoInitials}
            </div>
          )}
          <span className="font-bold text-gray-800 text-sm hidden md:block">Dashboard</span>
          <FaCog className="text-gray-400 text-xs" />
        </button>
        <div className="hidden md:flex items-center gap-2 bg-gray-100 rounded-lg px-3 py-2 w-72">
          <HiSearch className="text-gray-400 text-lg flex-shrink-0" />
          <input
            type="text"
            placeholder="Search..."
            className="bg-transparent text-sm text-gray-700 placeholder-gray-400 outline-none w-full"
          />
        </div>
      </div>

      {/* Right */}
      <div className="flex items-center gap-2">
        {/* Notification Bell */}
        <button className="relative w-9 h-9 flex items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 transition-colors">
          <FaBell className="text-lg" />
          <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-red-500 rounded-full" />
        </button>

        {/* Profile Dropdown */}
        <div ref={profileRef} className="relative">
          <button
            onClick={() => setProfileOpen(!profileOpen)}
            className="flex items-center gap-2 pl-2 pr-3 py-1.5 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <div className="w-8 h-8 rounded-full bg-indigo-600 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
              {initials}
            </div>
            <div className="hidden md:block text-left">
              <p className="text-sm font-semibold text-gray-800 leading-tight">
                {user?.name || "Admin"}
              </p>
              <p className="text-xs text-gray-500 leading-tight">{user?.email || ""}</p>
            </div>
            <FaChevronDown
              className={`hidden md:block text-xs text-gray-400 transition-transform duration-200 ${profileOpen ? "rotate-180" : ""}`}
            />
          </button>

          {profileOpen && (
            <div className="absolute right-0 mt-1 w-52 bg-white rounded-xl border border-gray-200 shadow-xl py-1 z-50">
              <div className="px-4 py-3 border-b border-gray-100">
                <p className="font-semibold text-gray-900 text-sm">{user?.name || "Admin"}</p>
                <p className="text-xs text-gray-500 truncate">{user?.email || ""}</p>
              </div>
              <div className="py-1">
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
                >
                  <FaSignOutAlt />
                  Logout
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>

    {showLogoEdit && (
      <LogoEditModal
        currentLogo={logoSrc}
        currentInitials={logoInitials}
        onClose={() => setShowLogoEdit(false)}
        onSave={handleSaveLogo}
      />
    )}
    </>
  );
};

export default DashboardNav;
